import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { forEach, get, isEmpty, map, isUndefined, merge, omit, sortBy, set, isNil } from 'lodash';
import { mapFormFields, reduceToValues, updateFormFields, fieldsHasErrors } from '../../../lib/form';
import Button from '../../../common/components/Button';
import TextField from '../../../common/components/c_TextField';
import MaskedTextField from '../../../common/components/c_MaskedTextField';
import DropDownMenu from '../../../common/components/c_DropDownMenu';
import ToggleButton from '../../../common/components/c_ToggleButton';
import DropdownChecklist from '../../../common/components/DropdownChecklist';
import CheckListTextField from '../../../common/components/CheckListTextField';
import * as companyApi from '../../../redux/actionCreators/async/companies';
import * as companyStore from '../../../redux/actionCreators/sync/companies';
import { getCompanyById } from '../../../redux/actionCreators/async';
import { setNavState } from '../../../redux/actionCreators/sync';
import * as ui from '../../../redux/actionCreators/sync/ui';
import { validateFormField } from '../../../modelValidator';

import { parentChildList } from '../../../lib/parentChildList';
import { activeSegmentTypes } from '../../../lib/industrySegments';

export const companyFields = {
  required : ['name', 'state',
              'numberOfEmployees',
              'segmentTypes', 'industryTypes', 'phoneNumber'],
  optional : ['address2', 'parentCompany', 'parentCompanyId',
              'address', 'city', 'zip', 'numberOfOffices', 'phoneNumber',
              'segmentOther', 'industryOther', 'associationOther',
              'isDivisionOrSubsidiary', 'tradeAssociations',
              'iiabaState', 'affiliationOther', 'affiliationTypes', 'timezone', 'website']
};

const parentCompanyFields = {
  required : ['name'],
  optional : ['address2', 'address', 'city', 'state', 'zip']
};

const yesNo = [{value: true, primaryText: 'Yes'}, {value: false, primaryText: 'No'}];

function initializeCompanyForm(company) {
  const companyForm = mapFormFields(company, companyFields.required, companyFields.optional);
  const {
    parentCompany
  } = companyForm;
  const parentCompanyForm = mapFormFields({ name: parentCompany }, parentCompanyFields.required, parentCompanyFields.optional);
  return {
    companyForm,
    parentCompanyForm
  };
}

@connect((state) => ({
  contact: state.contactProfile,
  company: state.contactProfile.company,
  staticData: state.staticData,
  labels: state.labels
}))
export default class ClientCompanyInfo extends React.Component {
  static propTypes = {
    dispatch : PropTypes.func.isRequired,
    contact : PropTypes.object.isRequired,
    company: PropTypes.object,
    staticData: PropTypes.object,
    labels: PropTypes.object,
  }

  constructor(props, context) {
    super(props, context);
    const {
      companyForm,
      parentCompanyForm
    } = initializeCompanyForm(props.company);

    this.state = {
      companyForm,
      parentCompanyForm,
      showErrors: false,
      showForm: !isEmpty(props.company) // use this so we don't load the form with empty contact data on initial load
    };

    if(this.state.showForm) {
      this.validateCompanyForm(this.state.companyForm);
    }

    this.parentChildHandlers = parentChildList();


  }

  componentDidMount() {
    this.props.dispatch(setNavState({isOpen: true, hideClose: true}));

    const parentCompanyId = get(this.props, 'company.parentCompanyId');
    if (this.props.company && parentCompanyId) {
      this.loadParentCompany(parentCompanyId);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps && nextProps.company) {
      const {
        companyForm,
        parentCompanyForm
      } = initializeCompanyForm(nextProps.company);

      this.validateCompanyForm(companyForm);

      this.setState(merge({...this.state}, {companyForm, parentCompanyForm, showForm: true}));

      const parentCompanyId = get(this.nextProps, 'company.parentCompanyId');
      if (!this.state.parentCompanyData && parentCompanyId) {
        this.loadParentCompany(parentCompanyId);
      }
    }
  }

  goToDashboard() {
    this.props.dispatch(push('/client/company'));
  }

  loadParentCompany(parentCompanyId) {
    if (parentCompanyId) {
      this.props.dispatch(getCompanyById(this.props.company.parentCompanyId,  {
        success :(data) => {
          this.setState(merge({}, this.state, {
            parentCompanyForm: mapFormFields(data[0], parentCompanyFields.required, parentCompanyFields.optional)
          }));
        }
      }));
    }
  }

  hasErrors(companyForm) {
    return fieldsHasErrors(companyForm);
  }

  iiabaStatesInternal() {
    return [{ id: null, name: 'Select' },
      ...(get(this.props, 'staticData.iiabaStates', []))];
  }

  validateCompanyForm(form) {
    forEach(form, (prop, key) => {
      if (companyFields.required.includes(key)) {
        set(form, `${key}.errors`,  validateFormField(key, form));
      }
    });
    this.validatePhoneNumber(form);
  }

  validatePhoneNumber(form) {
    const pattern = new RegExp(/^[2-9]\d{9}$/);
    const phoneNumber = get(form, 'phoneNumber.value');
    if (phoneNumber!=='') {
      if (pattern.test(phoneNumber)===false) {
        set(form, 'phoneNumber.errors', ['Must be a valid phone number.']);
      }
    }
  }

  handleCompanyUpdate = (event, prop, value) => {

    if (prop==='segmentTypes' && isNil(value)) return; // WPS-43 - do not clear segment types when parent changes

    const companyForm = get(merge({}, this.state), 'companyForm', {});
    const newValue = !isUndefined(value) ? value : get(event, 'target.value');

    if (prop === 'isDivisionOrSubsidiary') {
      set(companyForm, 'isDivisionOrSubsidiary.value',  newValue);
    } else if (['numberOfOffices', 'numberOfEmployees'].includes(prop)) {
      updateFormFields(companyForm, prop, Math.abs(newValue));
    } else {
      updateFormFields(companyForm, prop, newValue);
    }

    const showErrors = !!this.hasErrors(companyForm);
    this.setState(merge({}, omit(this.state, ['companyForm']), {companyForm, showErrors}));
  }

  handleSubmitCompanyForm = () => {
    const { dispatch, contact, company } = this.props;
    const companyForm =  merge({}, this.state.companyForm);
    this.validateCompanyForm(companyForm);
    const showErrors = this.hasErrors(companyForm);
    const newCompany = reduceToValues(companyForm);

    if (showErrors) {
      return this.setState(merge({}, {...this.state}, {companyForm, showErrors}));
    }
    if (!isEmpty(newCompany)) {
      newCompany.id = get(company, 'id');
      newCompany.putByClient = true;
      dispatch(companyApi.updateCompanyById(newCompany, (err, {body:{data:{items:[companyData]}}}) => {
        if (err) {
          return console.error(err);
        }

        // remove the putByClient because it was only needed for the update, do not reflect it in the store.
        delete newCompany.putByClient;

        dispatch(companyStore.updateCompany(merge({}, {company: newCompany})));
        dispatch(ui.showSnackBar());
        this.goToDashboard();
      }));
    }
    else {
      this.goToDashboard();
    }
  }

  handleCancel = () => {
    this.goToDashboard();
  }

  handleOtherChange(prop, otherProp, menuItems = [], handleUpdate) {
    return (event, value) => {

      if(handleUpdate) {
        handleUpdate(event, value);
      }

      setTimeout(() => {
        if (!this.renderOtherField(prop, menuItems)) {
          this.handleCompanyUpdate(event, otherProp, null);
        }
      });
    };
  }

  handleChange(prop) {
    return (event, value) => this.handleCompanyUpdate(event, prop, value);
  }

  handleHideSnackBar = () => {
    if (!this.state.showErrors) {
      this.goToDashboard();
    }
  }

  renderFieldProperties(model, prop, label) {
    const field = get(model, prop, {});

    return {
      'data-id': prop,
      defaultValue: field.value,
      floatingLabelText: label,
      isDirty: !!(field.required ? get(field, 'errors[0]') : undefined),
      errorText: field.required ? get(field, 'errors[0]') : undefined,
      onBlur: this.handleChange(prop)
    };
  }

  renderCompanyFieldProperties(prop, label, menuItems = []) {
    sortBy(menuItems, ['id']);
    return merge({}, this.renderFieldProperties(this.state.companyForm, prop, label), {menuItems});
  }

  renderCompanyFieldWithOtherProperties(prop, otherProp, label, menuItems = []) {
    const fieldProps = this.renderCompanyFieldProperties(prop, label, menuItems);
    const { onChange } = fieldProps;
    fieldProps.dataId = prop;
    fieldProps.onChange = this.handleOtherChange(prop, otherProp, menuItems, onChange);
    return fieldProps;
  }

  renderParentCompanyFieldProperties(prop, label, menuItems = []) {
    sortBy(menuItems, ['id']);
    return merge(this.renderFieldProperties(this.state.parentCompanyForm, prop, label), {menuItems}, {disabled: true});
  }

  renderOtherField(prop, menuItems = []) {
    const otherItem = menuItems.find(i => i.name === 'Other' || i.primaryText === 'Other');
    const fieldValue = get(this.state.companyForm, `${prop}.value`, get(this.state.companyForm, `${prop}.defaultValue`, []));
    return otherItem && fieldValue && fieldValue.length && fieldValue.includes(otherItem.id || otherItem.value);
  }

  render() {

    const {
      Client : {
        Company : {
          editCompany
        }
      },
      CompanyCard
    } = this.props.labels;

    const {
      industryTypesList,
      segmentTypes
    } = this.props.staticData;

    const {parent: industryTypeProps, child: segmentTypeProps} = this.parentChildHandlers({
      parentProps: this.renderCompanyFieldWithOtherProperties('industryTypes', 'industryOther', CompanyCard.industry, this.props.staticData.industryTypesList),
      childProps: this.renderCompanyFieldWithOtherProperties('segmentTypes', 'segmentOther', 'Industry Segment', this.props.staticData.segmentTypes),
    });

    const selectedIndustries = get(this.state, 'companyForm.industryTypes.value', get(this.state, 'companyForm.industryTypes.defaultValue'));
    const activeSegments = activeSegmentTypes(segmentTypes, industryTypesList, selectedIndustries, get(this.state, 'companyForm.segmentTypes.defaultValue'));

    return (
      <div className='client-company-info'>
        <h1>Company Information</h1>
        {this.state.showForm
          &&
          <div className='form-fields'>
            <div className='form-section'>
              <TextField
                className='center-column-1'
                {...this.renderCompanyFieldProperties('name', 'Company Name')} />
              <TextField
                className='center-column-1'
                {...this.renderCompanyFieldProperties('address', 'Address Line 1')} />
              <TextField
                className='center-column-1'
                {...this.renderCompanyFieldProperties('address2', 'Address Line 2')} />
              <TextField
                className='center-column-1'
                {...this.renderCompanyFieldProperties('city', 'City')} />
              <DropDownMenu
                className='inline-dropdown-container center-column-1'
                {...this.renderCompanyFieldProperties('state', 'State', this.props.staticData.stateList)} />
              <TextField
                className='center-column-1' type='number' maxLength='5'
                {...this.renderCompanyFieldProperties('zip', 'Zip')} />
              <MaskedTextField className='center-column-1'
                hideMaskChar
                {...this.renderCompanyFieldProperties('phoneNumber', 'Phone Number')}
                mask='(999) 999-9999'/>
              <TextField
                className='center-column-1'
                type='number'
                min={0}
                {...this.renderCompanyFieldProperties('numberOfOffices', 'Number of Offices')}
              />
              <TextField
                className='center-column-1'
                type='number'
                min={0}
                {...this.renderCompanyFieldProperties('numberOfEmployees', 'Number of Employees')}
              />
              <DropDownMenu
                className='inline-dropdown-container center-column-1'
                {...this.renderCompanyFieldProperties('timezone', 'Timezone', this.props.staticData.timezoneList)} />

                <TextField
                  className='center-column-1'
                  {...this.renderCompanyFieldProperties('website', 'Company Website')} />
            </div>

            <div className='form-section'>
              <div className='center-column-3 text-only-section'>Are you a division or subsidiary of another company?</div>
              <ToggleButton
                className='center-column-1 division-toggle'
                {...this.renderCompanyFieldProperties('isDivisionOrSubsidiary', undefined, yesNo)}
              />
            </div>

            { !!get(this.state, 'companyForm.isDivisionOrSubsidiary.value', false)
              &&
              <div className='form-section company-parent-info'>
                {
                  !!get(this.state, 'companyForm.parentCompanyId.value', false)
                  &&
                  <TextField
                    className='center-column-1'
                    {...this.renderParentCompanyFieldProperties('name', 'Parent Company Name')} />
                  ||
                  <TextField
                    className='center-column-1'
                    {...this.renderCompanyFieldProperties('parentCompany', 'Parent Company Name')} />
                }
                <TextField
                  className='center-column-1'
                  {...this.renderParentCompanyFieldProperties('address', 'Address Line 1')} />
                <TextField
                  className='center-column-1'
                  {...this.renderParentCompanyFieldProperties('address2', 'Address Line 2')} />
                <TextField
                  className='center-column-1'
                  {...this.renderParentCompanyFieldProperties('city', 'City')} />
                <TextField
                  className='center-column-1'
                  {...this.renderParentCompanyFieldProperties('state', 'State')} />
                <TextField
                  className='center-column-1'
                  {...this.renderParentCompanyFieldProperties('zip', 'Zip')} />
              </div>
            }

            <div className='grid form-section industry-section'>
              <div className='industry'>
              <CheckListTextField
                className='row'
                checkListContainerClassName='col'
                textfieldContainerClassName='col grow-3'
                showLabel
                  {...industryTypeProps}
                />
                {

                  _.get(this.props.labels,'Client.Company.editCompany.industryTypesSubText')
									&&
                  <div className='label'>{_.get(this.props.labels,'Client.Company.editCompany.industryTypesSubText')}</div>
                }
                {
                  this.renderOtherField('industryTypes', this.props.staticData.industryTypesList)
                  &&
                  <div className='row'>
                        <div className='col'></div>
                        <div className='col grow-3'>
                          <TextField
                            className='center-column-1'
                            {...this.renderCompanyFieldProperties('industryOther', 'Please specify')} />
                        </div>
                    </div>
                }
              </div>
              <div className='industry'>
                <DropdownChecklist
                  data-id={'segmentTypes'}
                  textLabel={'Industry Segment'}
                  menuItems={activeSegments}
                  value={get(this.state, 'companyForm.segmentTypes.value')}
                  groupBy={'parentName'}
                  errorText={get(this.state, 'companyForm.segmentTypes.errors[0]') || 'This field is required'}
                  dirty={!!get(this.state, 'companyForm.industryTypes.value', false)}
                  onChange={this.handleChange('segmentTypes')}
                  required
                />
                {
                  _.get(this.props.labels,'Client.Company.editCompany.segmentTypesSubText')
                  &&
                  <div className='label'>{_.get(this.props.labels,'Client.Company.editCompany.segmentTypesSubText')}</div>
                }
                {
                  !!this.renderOtherField('segmentTypes', this.props.staticData.segmentTypes)
                  &&
                  <div className='row'>
                  <div className='col'></div>
                    <div className='col grow-3'>
                        <TextField
                          className='center-column-1'
                          {...this.renderCompanyFieldProperties('segmentOther', 'Please specify')} />
                    </div>
                  </div>
                }
              </div>
              {
                get(this.props.labels, 'tradeAssociations')
                &&
                <div className='industry'>
                  <CheckListTextField
                    className='row'
                    checkListContainerClassName='col'
                    textfieldContainerClassName='col grow-3'
                    dataId='tradeAssociationsDisplay'
                    showLabel
                    {...this.renderCompanyFieldWithOtherProperties(
                      'tradeAssociations', 'associationOther',
                      get(this.props.labels, 'tradeAssociations'),
                      map(this.props.staticData.tradeAssociations, ({id, crmName}) => ({id, name: crmName}))
                    )}
                  />
                  {
                    this.renderOtherField('tradeAssociations', this.props.staticData.tradeAssociations)
                    &&
                    <div className='row'>
                      <div className='col'></div>
                      <div className='col grow-3'>
                        <TextField
                          className='center-column-1'
                          {...this.renderCompanyFieldProperties('associationOther', 'Please specify')} />
                        </div>
                      </div>
                    }
                  </div>
              }
              <div className='industry'>
                {
                  get(editCompany, 'affiliationTypes')
                  &&
                  <CheckListTextField
                    className='row'
                    checkListContainerClassName='col'
                    textfieldContainerClassName='col grow-3'
                    showLabel
                    {...this.renderCompanyFieldWithOtherProperties('affiliationTypes', 'affiliationOther', 'Network Affiliations', this.props.staticData.affiliationTypes)}
                  />
                }
                  {
                    this.renderOtherField('affiliationTypes', this.props.staticData.affiliationTypes)
                    &&
                     <div className='row'>
                        <div className='col'></div>
                        <div className='col grow-3'>
                          <TextField
                            className='center-column-1'
                            {...this.renderCompanyFieldProperties('affiliationOther', 'Please specify')} />
                        </div>
                    </div>
                  }
              </div>
              <div className='industry'>
                {
                  get(editCompany, 'iiabaStates')
                  &&
                  <DropDownMenu
                    {...this.renderCompanyFieldProperties('iiabaState', 'State Association Memberships to IIABA', this.iiabaStatesInternal())}
                  />
                }
              </div>
            </div>
          </div>
        }
        <ul className='form-submission-container'>
          <li className='submission-button'>
            {this.state.showErrors && <div className='error-message'>Please fill out missing fields.</div>}
          </li>
          <li className='submission-button'>
            <Button
              className='button-secondary-large'
              label='Cancel'
              onClick={this.handleCancel}
              />
          </li>
          <li className='submission-button'>
            <Button
              className='button-main-large'
              label='Save'
              onClick={this.handleSubmitCompanyForm}
              />
          </li>
        </ul>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./shared/react/client/ClientCompanyInfo/index.js